import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PortalLayout } from '../components/PortalLayout';
import { api } from '../api';

/* ── Types ─────────────────────────────────────────────────────── */
interface Ops {
  pipeline: Record<string, number>;
  todays_jobs: TodayJob[];
  revenue: {
    week: number; month: number; pipeline_value: number;
    pending_quotes: number; pending_quote_value: number;
    uninvoiced_count: number; uninvoiced_value: number;
  };
  fleet: {
    total_vehicles: number; vehicles_active: number;
    total_drivers: number; drivers_active: number;
  };
  alerts: Alert[];
  upcoming_jobs: UpcomingJob[];
}

interface TodayJob {
  id: number; job_number: string; status: string; start_time: string | null;
  customer_name: string | null; driver_name: string | null; vehicle_reg: string | null;
  collection_postcode: string | null; delivery_postcode: string | null;
  total_distance_miles: number | null;
}

interface UpcomingJob {
  id: number; job_number: string; status: string; job_date: string;
  start_time: string | null; customer_name: string | null;
  collection_postcode: string | null; delivery_postcode: string | null;
}

interface Alert {
  type: string; severity: string; message: string; link: string;
}

interface TrackingVehicle {
  registration: string;
  location?: string;
  street?: string;
  status?: string;
  speed?: number;
  latitude?: number;
  longitude?: number;
}

interface ComplianceData {
  vehicles: VehicleCompliance[];
  drivers: DriverCompliance[];
  infringement_summary: {
    open: number; resolved: number; total: number;
    by_type: Record<string, number>;
  };
}

interface VehicleCompliance {
  id: number; registration: string;
  mot_due: string | null; mot_status: string | null;
  tax_due: string | null; tax_status: string | null;
  insurance_due: string | null; insurance_status: string | null;
  tacho_calibration_due: string | null; tacho_status: string | null;
  speed_limiter_due: string | null; speed_limiter_status: string | null;
  next_pmi_due: string | null; pmi_status: string | null;
}

interface DriverCompliance {
  id: number; name: string;
  card_expiry: string | null; card_status: string | null;
  last_download_date: string | null; days_since_download: number | null;
  download_status: string;
}

/* ── Helpers ───────────────────────────────────────────────────── */
const STATUS_LABEL: Record<string, string> = {
  awaiting_assignment: 'Awaiting', scheduled: 'Awaiting',
  booked_in: 'Booked', assigned: 'Assigned',
  dispatched: 'Dispatched', in_progress: 'In Progress',
  completed: 'Completed', invoiced: 'Invoiced',
};

function fmtDate(d: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d + 'T00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return d; }
}

function fmtCurrency(n: number): string {
  if (n >= 10000) return `£${(n / 1000).toFixed(1)}k`;
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function statusBadge(status: string | null): string {
  if (!status) return 'text-gray-400';
  if (status === 'overdue' || status === 'expired') return 'text-red-700 font-semibold';
  if (status === 'due_soon' || status === 'expiring_soon') return 'text-amber-600 font-medium';
  return 'text-green-700';
}

function statusBg(status: string | null): string {
  if (!status) return '';
  if (status === 'overdue' || status === 'expired') return 'bg-red-50';
  if (status === 'due_soon' || status === 'expiring_soon') return 'bg-amber-50';
  return '';
}

function statusDot(status: string | null): string {
  if (!status) return 'bg-gray-300';
  if (status === 'overdue' || status === 'expired' || status === 'never') return 'bg-red-500';
  if (status === 'due_soon' || status === 'expiring_soon') return 'bg-amber-400';
  return 'bg-green-500';
}

function statusText(status: string | null): string {
  if (!status) return '—';
  const map: Record<string, string> = {
    overdue: 'OVERDUE', due_soon: 'Due Soon', ok: 'OK',
    expired: 'EXPIRED', expiring_soon: 'Expiring Soon',
    never: 'Never',
  };
  return map[status] || status;
}

/* ── Main ──────────────────────────────────────────────────────── */
export function HomePage() {
  const { user } = useAuth();
  const [ops, setOps] = useState<Ops | null>(null);
  const [compliance, setCompliance] = useState<ComplianceData | null>(null);
  const [vehicleLocations, setVehicleLocations] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'compliance' | 'operations'>('compliance');

  useEffect(() => {
    (async () => {
      try {
        const [opsData, compData] = await Promise.all([
          api.getOperations(),
          api.getCompliance(),
        ]);
        setOps(opsData);
        setCompliance(compData);

        // Fetch live vehicle locations from RAM Tracking (non-blocking)
        try {
          const TRACKING_BASE = import.meta.env.VITE_API_URL
            ? `${import.meta.env.VITE_API_URL}` : '';
          const token = localStorage.getItem('tourx_token');
          const trackRes = await fetch(`${TRACKING_BASE}/api/tracking/vehicles`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (trackRes.ok) {
            const trackData = await trackRes.json();
            const locMap: Record<string, string> = {};
            (trackData.vehicles || []).forEach((v: TrackingVehicle) => {
              const reg = (v.registration || '').replace(/\s/g, '').toUpperCase();
              locMap[reg] = v.location || v.street || 'Unknown';
            });
            setVehicleLocations(locMap);
          }
        } catch { /* tracking data is optional */ }
      } catch { /* non-critical */ }
      finally { setLoading(false); }
    })();
  }, []);

  // Derived counts
  const overdueVehicles = compliance?.vehicles.filter(v =>
    [v.mot_status, v.tax_status, v.insurance_status, v.tacho_status, v.pmi_status].includes('overdue')
  ).length || 0;

  const overdueDownloads = compliance?.drivers.filter(d =>
    d.download_status === 'overdue' || d.download_status === 'never'
  ).length || 0;

  const pipeActive = ops ? Object.entries(ops.pipeline)
    .filter(([s]) => !['completed', 'invoiced', 'cancelled'].includes(s))
    .reduce((sum, [, n]) => sum + n, 0) : 0;

  const criticalAlerts = ops?.alerts.filter(a => a.severity === 'critical').length || 0;

  return (
    <PortalLayout currentPage="home">
      <div className="flex flex-col h-screen">
        {/* ── Top bar ──────────────────────────────────────── */}
        <div className="h-12 bg-white border-b border-gray-200 flex items-center justify-between px-5 flex-shrink-0">
          <div className="flex items-center gap-4">
            <h1 className="text-sm font-semibold text-gray-900">Dashboard</h1>
            <div className="flex border border-gray-300 rounded overflow-hidden">
              <button
                onClick={() => setActiveSection('compliance')}
                className={`px-3 py-1 text-xs font-medium transition ${
                  activeSection === 'compliance'
                    ? 'bg-slate-800 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >Compliance</button>
              <button
                onClick={() => setActiveSection('operations')}
                className={`px-3 py-1 text-xs font-medium transition border-l border-gray-300 ${
                  activeSection === 'operations'
                    ? 'bg-slate-800 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >Operations</button>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span className="text-gray-300">|</span>
            <span>{user?.company_name}</span>
          </div>
        </div>

        {/* ── Content ──────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-20 text-sm text-gray-400">Loading...</div>
          ) : (
            <>
              {/* ── KPI Strip ──────────────────────────────── */}
              <div className="grid grid-cols-6 gap-3 mb-5">
                <KPI label="Active Drivers" value={String(ops?.fleet.total_drivers || 0)} />
                <KPI label="Fleet Vehicles" value={String(ops?.fleet.total_vehicles || 0)} />
                <KPI label="Open Infringements" value={String(compliance?.infringement_summary.open || 0)}
                  alert={!!compliance && compliance.infringement_summary.open > 0} />
                <KPI label="Overdue Vehicles" value={String(overdueVehicles)} alert={overdueVehicles > 0} />
                <KPI label="Downloads Overdue" value={String(overdueDownloads)} alert={overdueDownloads > 0} />
                <KPI label="Active Jobs" value={String(pipeActive)} />
              </div>

              {/* ── Critical alerts strip ──────────────────── */}
              {criticalAlerts > 0 && (
                <div className="mb-5 bg-red-50 border border-red-200 rounded px-4 py-2.5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    <span className="text-xs font-semibold text-red-800 uppercase tracking-wide">
                      {criticalAlerts} Critical Alert{criticalAlerts !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="space-y-0.5 pl-4">
                    {ops!.alerts.filter(a => a.severity === 'critical').slice(0, 5).map((a, i) => (
                      <a key={i} href={a.link} className="block text-xs text-red-700 hover:text-red-900 hover:underline">{a.message}</a>
                    ))}
                  </div>
                </div>
              )}

              {activeSection === 'compliance' ? (
                <ComplianceView compliance={compliance} ops={ops} vehicleLocations={vehicleLocations} />
              ) : (
                <OperationsView ops={ops} vehicleLocations={vehicleLocations} />
              )}
            </>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}


/* ══════════════════════════════════════════════════════════════════
   COMPLIANCE VIEW
   ══════════════════════════════════════════════════════════════════ */

function ComplianceView({ compliance, ops, vehicleLocations }: { compliance: ComplianceData | null; ops: Ops | null; vehicleLocations: Record<string, string> }) {
  if (!compliance) return <div className="text-sm text-gray-400">No compliance data available</div>;

  return (
    <div className="space-y-5">
      {/* Row 1: Vehicle Compliance + Driver Downloads */}
      <div className="grid grid-cols-2 gap-5">
        {/* Vehicle Compliance Table */}
        <Panel title="Vehicle Compliance" count={compliance.vehicles.length} linkLabel="Fleet" linkHref="/fleet">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 text-left">
                  <th className="py-2 pr-3 font-semibold text-gray-600">Registration</th>
                  <th className="py-2 pr-3 font-semibold text-gray-600">Current Location</th>
                  <th className="py-2 pr-3 font-semibold text-gray-600">MOT</th>
                  <th className="py-2 pr-3 font-semibold text-gray-600">Tax</th>
                  <th className="py-2 pr-3 font-semibold text-gray-600">Insurance</th>
                  <th className="py-2 pr-3 font-semibold text-gray-600">Tacho Cal</th>
                  <th className="py-2 font-semibold text-gray-600">PMI</th>
                </tr>
              </thead>
              <tbody>
                {compliance.vehicles.length === 0 ? (
                  <tr><td colSpan={7} className="py-6 text-center text-gray-400">No vehicles</td></tr>
                ) : compliance.vehicles.slice(0, 12).map(v => {
                  const worstStatus = [v.mot_status, v.tax_status, v.insurance_status, v.tacho_status, v.pmi_status];
                  const hasOverdue = worstStatus.includes('overdue');
                  const regKey = v.registration.replace(/\s/g, '').toUpperCase();
                  const loc = vehicleLocations[regKey];
                  return (
                    <tr key={v.id} className={`border-b border-gray-100 ${hasOverdue ? 'bg-red-50/50' : ''}`}>
                      <td className="py-1.5 pr-3 font-medium text-gray-900">{v.registration}</td>
                      <td className="py-1.5 pr-3 text-[10px] text-gray-500 max-w-[140px] truncate" title={loc || ''}>
                        {loc ? (
                          <span className="flex items-center gap-1">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0"></span>
                            {loc}
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-1.5 pr-3"><DateCell date={v.mot_due} status={v.mot_status} /></td>
                      <td className="py-1.5 pr-3"><DateCell date={v.tax_due} status={v.tax_status} /></td>
                      <td className="py-1.5 pr-3"><DateCell date={v.insurance_due} status={v.insurance_status} /></td>
                      <td className="py-1.5 pr-3"><DateCell date={v.tacho_calibration_due} status={v.tacho_status} /></td>
                      <td className="py-1.5"><DateCell date={v.next_pmi_due} status={v.pmi_status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {compliance.vehicles.length > 12 && (
              <a href="/fleet" className="block text-center text-xs text-blue-600 hover:underline py-2 border-t border-gray-100">
                View all {compliance.vehicles.length} vehicles
              </a>
            )}
          </div>
        </Panel>

        {/* Driver Download Status */}
        <Panel title="Driver Card Downloads" count={compliance.drivers.length} linkLabel="Drivers" linkHref="/drivers">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 text-left">
                  <th className="py-2 pr-3 font-semibold text-gray-600">Driver</th>
                  <th className="py-2 pr-3 font-semibold text-gray-600">Last Download</th>
                  <th className="py-2 pr-3 font-semibold text-gray-600 text-right">Days</th>
                  <th className="py-2 pr-3 font-semibold text-gray-600">Status</th>
                  <th className="py-2 font-semibold text-gray-600">Card Expiry</th>
                </tr>
              </thead>
              <tbody>
                {compliance.drivers.length === 0 ? (
                  <tr><td colSpan={5} className="py-6 text-center text-gray-400">No drivers</td></tr>
                ) : compliance.drivers.slice(0, 12).map(d => (
                  <tr key={d.id} className={`border-b border-gray-100 ${
                    d.download_status === 'overdue' || d.download_status === 'never' ? 'bg-red-50/50' : ''
                  }`}>
                    <td className="py-1.5 pr-3 font-medium text-gray-900">{d.name}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{d.last_download_date ? fmtDate(d.last_download_date) : '—'}</td>
                    <td className="py-1.5 pr-3 text-right text-gray-600">{d.days_since_download ?? '—'}</td>
                    <td className="py-1.5 pr-3">
                      <span className={`inline-flex items-center gap-1 ${statusBadge(d.download_status)}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusDot(d.download_status)}`} />
                        {statusText(d.download_status)}
                      </span>
                    </td>
                    <td className="py-1.5">
                      <span className={statusBadge(d.card_status)}>
                        {d.card_expiry ? fmtDate(d.card_expiry) : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {compliance.drivers.length > 12 && (
              <a href="/drivers" className="block text-center text-xs text-blue-600 hover:underline py-2 border-t border-gray-100">
                View all {compliance.drivers.length} drivers
              </a>
            )}
          </div>
        </Panel>
      </div>

      {/* Row 2: Infringements + Alerts */}
      <div className="grid grid-cols-3 gap-5">
        {/* Infringement Summary */}
        <Panel title="Infringements">
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center py-3 bg-gray-50 rounded">
                <p className="text-lg font-bold text-red-700">{compliance.infringement_summary.open}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Open</p>
              </div>
              <div className="text-center py-3 bg-gray-50 rounded">
                <p className="text-lg font-bold text-green-700">{compliance.infringement_summary.resolved}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Resolved</p>
              </div>
              <div className="text-center py-3 bg-gray-50 rounded">
                <p className="text-lg font-bold text-gray-700">{compliance.infringement_summary.total}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Total</p>
              </div>
            </div>

            {Object.keys(compliance.infringement_summary.by_type).length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Open by Type</p>
                <div className="space-y-1">
                  {Object.entries(compliance.infringement_summary.by_type)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 8)
                    .map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600 truncate pr-2">{type}</span>
                        <span className="font-semibold text-gray-800 flex-shrink-0">{count}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            <a href="/compliance" className="block text-xs text-blue-600 hover:underline pt-1">
              View DCRS &amp; infringement management
            </a>
          </div>
        </Panel>

        {/* Compliance Alerts */}
        <Panel title="Compliance Alerts" count={ops?.alerts.length}>
          {!ops || ops.alerts.length === 0 ? (
            <div className="py-6 text-center">
              <p className="text-xs text-green-700 font-medium">All clear — no compliance alerts</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {ops.alerts.map((a, i) => (
                <a key={i} href={a.link}
                  className={`block px-2.5 py-2 rounded text-xs transition hover:opacity-80 ${
                    a.severity === 'critical' ? 'bg-red-50 text-red-800' :
                    a.severity === 'warning' ? 'bg-amber-50 text-amber-800' :
                    'bg-blue-50 text-blue-800'
                  }`}>
                  <div className="flex items-start gap-1.5">
                    <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      a.severity === 'critical' ? 'bg-red-500' : a.severity === 'warning' ? 'bg-amber-400' : 'bg-blue-400'
                    }`} />
                    <span>{a.message}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </Panel>

        {/* Quick Operations Summary */}
        <Panel title="Operations Summary">
          {ops && (
            <div className="space-y-3">
              <div>
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Job Pipeline</p>
                <div className="space-y-1">
                  {[
                    { key: 'awaiting_assignment', label: 'Awaiting Assignment' },
                    { key: 'booked_in', label: 'Booked In' },
                    { key: 'assigned', label: 'Assigned' },
                    { key: 'dispatched', label: 'Dispatched' },
                    { key: 'in_progress', label: 'In Progress' },
                  ].map(s => {
                    const count = (ops.pipeline[s.key] || 0) + (s.key === 'awaiting_assignment' ? (ops.pipeline['scheduled'] || 0) : 0);
                    return (
                      <div key={s.key} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">{s.label}</span>
                        <span className="font-semibold text-gray-800">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-3">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Revenue</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">This Week</span>
                    <span className="font-semibold text-gray-800">{fmtCurrency(ops.revenue.week)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">This Month</span>
                    <span className="font-semibold text-gray-800">{fmtCurrency(ops.revenue.month)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Pipeline</span>
                    <span className="font-semibold text-gray-800">{fmtCurrency(ops.revenue.pipeline_value)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Awaiting Invoice</span>
                    <span className="font-semibold text-amber-700">{fmtCurrency(ops.revenue.uninvoiced_value)} ({ops.revenue.uninvoiced_count})</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-3">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Fleet Deployment</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Vehicles Active</span>
                    <span className="font-semibold text-gray-800">{ops.fleet.vehicles_active} / {ops.fleet.total_vehicles}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Drivers Deployed</span>
                    <span className="font-semibold text-gray-800">{ops.fleet.drivers_active} / {ops.fleet.total_drivers}</span>
                  </div>
                </div>
              </div>

              <a href="/jobs" className="block text-xs text-blue-600 hover:underline pt-1">Open Job Planner</a>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════
   OPERATIONS VIEW
   ══════════════════════════════════════════════════════════════════ */

function OperationsView({ ops, vehicleLocations }: { ops: Ops | null; vehicleLocations: Record<string, string> }) {
  if (!ops) return <div className="text-sm text-gray-400">No operations data available</div>;

  return (
    <div className="space-y-5">
      {/* Row 1: Today's Jobs */}
      <Panel title="Today's Jobs" count={ops.todays_jobs.length}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="py-2 pr-3 font-semibold text-gray-600">Job #</th>
                <th className="py-2 pr-3 font-semibold text-gray-600">Time</th>
                <th className="py-2 pr-3 font-semibold text-gray-600">Customer</th>
                <th className="py-2 pr-3 font-semibold text-gray-600">Route</th>
                <th className="py-2 pr-3 font-semibold text-gray-600">Driver</th>
                <th className="py-2 pr-3 font-semibold text-gray-600">Vehicle</th>
                <th className="py-2 pr-3 font-semibold text-gray-600">Current Location</th>
                <th className="py-2 font-semibold text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {ops.todays_jobs.length === 0 ? (
                <tr><td colSpan={8} className="py-6 text-center text-gray-400">No jobs scheduled for today</td></tr>
              ) : ops.todays_jobs.map(j => {
                const regKey = (j.vehicle_reg || '').replace(/\s/g, '').toUpperCase();
                const location = vehicleLocations[regKey];
                return (
                <tr key={j.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-1.5 pr-3 font-medium text-gray-900">{j.job_number}</td>
                  <td className="py-1.5 pr-3 text-gray-600">{j.start_time || '—'}</td>
                  <td className="py-1.5 pr-3 text-gray-700">{j.customer_name || '—'}</td>
                  <td className="py-1.5 pr-3 text-gray-600">
                    {j.collection_postcode || '?'} → {j.delivery_postcode || '?'}
                    {j.total_distance_miles ? ` (${j.total_distance_miles.toFixed(0)} mi)` : ''}
                  </td>
                  <td className="py-1.5 pr-3">{j.driver_name || <span className="text-amber-600">Unassigned</span>}</td>
                  <td className="py-1.5 pr-3 text-gray-600">{j.vehicle_reg || '—'}</td>
                  <td className="py-1.5 pr-3 text-xs text-gray-500 max-w-[160px] truncate" title={location || ''}>
                    {location ? (
                      <span className="flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0"></span>
                        {location}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="py-1.5">
                    <span className={`text-[11px] font-medium ${
                      j.status === 'in_progress' ? 'text-green-700' :
                      j.status === 'dispatched' ? 'text-purple-700' :
                      j.status === 'assigned' ? 'text-blue-700' :
                      'text-amber-600'
                    }`}>{STATUS_LABEL[j.status] || j.status}</span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* Row 2: Pipeline + Upcoming + Revenue */}
      <div className="grid grid-cols-3 gap-5">
        {/* Pipeline */}
        <Panel title="Job Pipeline">
          <div className="space-y-2">
            {[
              { key: 'awaiting_assignment', label: 'Awaiting Assignment' },
              { key: 'booked_in', label: 'Booked In' },
              { key: 'assigned', label: 'Assigned' },
              { key: 'dispatched', label: 'Dispatched' },
              { key: 'in_progress', label: 'In Progress' },
              { key: 'completed', label: 'Completed' },
              { key: 'invoiced', label: 'Invoiced' },
            ].map(s => {
              const count = (ops.pipeline[s.key] || 0) + (s.key === 'awaiting_assignment' ? (ops.pipeline['scheduled'] || 0) : 0);
              return (
                <div key={s.key} className="flex items-center justify-between text-xs py-1 border-b border-gray-50">
                  <span className="text-gray-600">{s.label}</span>
                  <span className="font-semibold text-gray-800 min-w-[24px] text-right">{count}</span>
                </div>
              );
            })}
          </div>
          <a href="/jobs" className="block text-xs text-blue-600 hover:underline mt-3">Open Job Planner</a>
        </Panel>

        {/* Upcoming 7 Days */}
        <Panel title="Next 7 Days" count={ops.upcoming_jobs.length}>
          {ops.upcoming_jobs.length === 0 ? (
            <div className="py-6 text-center text-gray-400 text-xs">No upcoming jobs</div>
          ) : (
            <div className="space-y-1">
              {ops.upcoming_jobs.map(j => (
                <div key={j.id} className="flex items-center gap-2 py-1.5 border-b border-gray-50 text-xs">
                  <span className="text-gray-400 w-16 flex-shrink-0">
                    {new Date(j.job_date + 'T00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' })}
                  </span>
                  <span className="font-medium text-gray-900 w-16 flex-shrink-0">{j.job_number}</span>
                  <span className="text-gray-600 truncate flex-1">{j.customer_name || '—'}</span>
                  <span className={`text-[10px] font-medium flex-shrink-0 ${
                    ['assigned', 'dispatched'].includes(j.status) ? 'text-green-700' : 'text-amber-600'
                  }`}>{STATUS_LABEL[j.status] || j.status}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Revenue & Commercial */}
        <Panel title="Commercial">
          <div className="space-y-2">
            <div className="flex justify-between text-xs py-1 border-b border-gray-50">
              <span className="text-gray-600">Revenue (7 days)</span>
              <span className="font-semibold text-gray-800">{fmtCurrency(ops.revenue.week)}</span>
            </div>
            <div className="flex justify-between text-xs py-1 border-b border-gray-50">
              <span className="text-gray-600">Revenue (30 days)</span>
              <span className="font-semibold text-gray-800">{fmtCurrency(ops.revenue.month)}</span>
            </div>
            <div className="flex justify-between text-xs py-1 border-b border-gray-50">
              <span className="text-gray-600">Pipeline Value</span>
              <span className="font-semibold text-gray-800">{fmtCurrency(ops.revenue.pipeline_value)}</span>
            </div>
            <div className="flex justify-between text-xs py-1 border-b border-gray-50">
              <span className="text-gray-600">Pending Quotes</span>
              <span className="font-semibold text-gray-800">{ops.revenue.pending_quotes} ({fmtCurrency(ops.revenue.pending_quote_value)})</span>
            </div>
            <div className="flex justify-between text-xs py-1 border-b border-gray-50">
              <span className="text-gray-600">Awaiting Invoice</span>
              <span className="font-semibold text-amber-700">{ops.revenue.uninvoiced_count} ({fmtCurrency(ops.revenue.uninvoiced_value)})</span>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-3">
            <a href="/quote" className="block text-xs text-blue-600 hover:underline">Quotes</a>
            <a href="/jobs" className="block text-xs text-blue-600 hover:underline">Jobs</a>
          </div>
        </Panel>
      </div>

      {/* Row 3: Fleet utilisation */}
      <div className="grid grid-cols-2 gap-5">
        <Panel title="Vehicle Utilisation">
          <UtilRow active={ops.fleet.vehicles_active} total={ops.fleet.total_vehicles} label="vehicles" />
        </Panel>
        <Panel title="Driver Utilisation">
          <UtilRow active={ops.fleet.drivers_active} total={ops.fleet.total_drivers} label="drivers" />
        </Panel>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ══════════════════════════════════════════════════════════════════ */

function Panel({ title, count, linkLabel, linkHref, children }: {
  title: string; count?: number | null; linkLabel?: string; linkHref?: string; children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <h2 className="text-xs font-semibold text-gray-800 uppercase tracking-wide">{title}</h2>
          {count != null && <span className="text-[10px] text-gray-400">({count})</span>}
        </div>
        {linkLabel && linkHref && (
          <a href={linkHref} className="text-[10px] text-blue-600 hover:underline font-medium">{linkLabel} →</a>
        )}
      </div>
      <div className="px-4 py-3">{children}</div>
    </div>
  );
}

function KPI({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div className={`bg-white border rounded px-3 py-2.5 ${alert ? 'border-red-200' : 'border-gray-200'}`}>
      <p className="text-[10px] text-gray-500 uppercase tracking-wide font-medium">{label}</p>
      <p className={`text-lg font-bold mt-0.5 ${alert ? 'text-red-700' : 'text-gray-900'}`}>{value}</p>
    </div>
  );
}

function DateCell({ date, status }: { date: string | null; status: string | null }) {
  if (!date) return <span className="text-gray-300">—</span>;
  return (
    <span className={statusBadge(status)}>
      {fmtDate(date)}
      {status === 'overdue' && <span className="ml-1 text-[9px]">!</span>}
    </span>
  );
}

function UtilRow({ active, total, label }: { active: number; total: number; label: string }) {
  const pct = total > 0 ? (active / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <span className="text-xl font-bold text-gray-900">{active}</span>
          <span className="text-sm text-gray-400 mx-1">/</span>
          <span className="text-sm text-gray-500">{total}</span>
        </div>
        <span className="text-sm font-semibold text-gray-600">{pct.toFixed(0)}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-sm overflow-hidden">
        <div className="h-full rounded-sm bg-slate-600 transition-all duration-500"
          style={{ width: `${Math.max(pct, active > 0 ? 4 : 0)}%` }} />
      </div>
      <p className="text-[10px] text-gray-400 mt-1.5">{active} {label} deployed · {total - active} available</p>
    </div>
  );
}
